<template>
  <div
    class="pass-history-view v3-pass-history behaviour-settings-positive-behaviours"
  >
    <h2 class="sr-only">
      {{ behaviourTerminology.positiveBehavioursSectionTitle }}
    </h2>
    <output class="sr-only" aria-live="polite" aria-atomic="true">{{
      liveAnnouncement
    }}</output>
    <Loader :is-processing="state.isLoading" />
    <ActionPanel class="mb-3 behaviour-core-value-action-panel">
      <template #left-content>
        <div
          class="d-flex flex-wrap align-items-end gap-3 flex-grow-1 min-w-0 behaviour-core-value-toolbar"
        >
          <div class="behaviour-core-value-search-field min-w-0">
            <label
              id="behaviour-positive-behaviours-search-label"
              class="v3-form-label d-block mb-1"
              for="behaviour-positive-behaviours-search"
            >
              {{ $t("common.search") }}
            </label>
            <InputField
              class="behaviour-core-value-search"
              prepend-icon="ri-search-line"
              :model-value="state.searchInput"
              :placeholder="$t('common.search')"
              input-type="search"
              id="behaviour-positive-behaviours-search"
              data-testid="behaviour-positive-behaviours-search"
              @update:model-value="onSearchFieldInput"
            />
          </div>
          <div class="behaviour-positive-behaviours-core-value-field min-w-0">
            <label
              id="behaviour-positive-behaviours-core-value-filter-label"
              class="v3-form-label d-block mb-1"
              for="behaviour-positive-behaviours-core-value-filter-input"
            >
              {{ behaviourTerminology.coreValue }}
            </label>
            <BehaviourCoreValueLazySelect
              :model-value="state.coreValueFilterValue"
              class="behaviour-positive-behaviours-core-value-filter"
              hide-floating-label
              labelled-by="behaviour-positive-behaviours-core-value-filter-label"
              :prepend-items="coreValueFilterPrependItems"
              :selected-option="state.coreValueFilterSelectedOption"
              input-id="behaviour-positive-behaviours-core-value-filter-input"
              data-testid="behaviour-positive-behaviours-core-value-filter"
              @update:model-value="onCoreValueFilterChange"
              @option-selected="onCoreValueFilterOptionSelected"
            />
          </div>
          <div class="behaviour-core-value-status-field min-w-0">
            <label
              id="behaviour-positive-behaviours-status-filter-label"
              class="v3-form-label d-block mb-1"
              for="behaviour-positive-behaviours-status-filter-input"
            >
              {{ $t("behaviour_settings.col_status") }}
            </label>
            <CustomSelect
              v-model="state.statusFilterValue"
              class="behaviour-core-value-status-filter"
              hide-floating-label
              labelled-by="behaviour-positive-behaviours-status-filter-label"
              :options="statusFilterOptions"
              :close-on-select="true"
              :searchable="false"
              :reduce="(item) => item.value"
              input-id="behaviour-positive-behaviours-status-filter-input"
              data-testid="behaviour-positive-behaviours-status-filter"
              @update:model-value="onStatusFilterChange"
            />
          </div>
        </div>
      </template>
      <template #right-content>
        <div
          class="d-flex align-items-center flex-wrap justify-content-end gap-2"
        >
          <button
            type="button"
            class="btn btn-link d-flex align-items-center p-0 text-primary behaviour-core-value-reset text-decoration-none"
            :disabled="state.isLoading"
            data-testid="behaviour-positive-behaviours-reset-filters"
            @click="resetFilters"
          >
            <i class="ri-filter-off-line" aria-hidden="true" />
            <span class="ms-1 text-decoration-underline">{{
              $t("behaviour_settings.reset_filters")
            }}</span>
          </button>
          <BaseButton
            rounded
            data-testid="behaviour-positive-behaviours-upload-csv"
            type="button"
            prepend-icon="ri-upload-2-line"
            :is-disabled="state.isLoading"
            @click="onUploadCsv"
          >
            {{ $t("behaviour_settings.upload_csv") }}
          </BaseButton>
          <BaseButton
            solid
            rounded
            data-testid="behaviour-positive-behaviours-add"
            type="button"
            @click="onAddPositiveBehaviour"
          >
            {{ behaviourTerminology.addPositiveBehaviour }}
          </BaseButton>
        </div>
      </template>
    </ActionPanel>

    <PerPage
      v-if="state.pagination && state.pagination.total > 0"
      :pagination="state.pagination"
      :showing-entries="true"
      @on-page-change="setPerPage"
    />

    <DataTable
      class="datatable bg-white text-center mt-3"
      add-table-classes="behaviour-positive-behaviours-datatable"
      :fields="tableFields"
      :items="state.tableItems"
      :sorter="{ external: true, resetable: false }"
      :sorter-value="sorterValue"
      :no-items-view="noItemsView"
      data-testid="behaviour-positive-behaviours-table"
      @update:sorter-value="onSortColumnChange"
    >
      <template #name="{ item }">
        <td class="text-start">{{ item.name }}</td>
      </template>
      <template #coreValueName="{ item }">
        <td class="text-start align-middle">
          <span
            v-if="item.coreValueId"
            class="behaviour-core-value-dot me-2"
            :class="indicatorDotClass(item)"
            :style="indicatorDotStyle(item)"
            aria-hidden="true"
          />
          <span v-if="item.coreValueId" class="visually-hidden">
            {{ indicatorAriaLabel(item) }}
          </span>
          {{ item.coreValueName }}
        </td>
      </template>
      <template #awardDescription="{ item }">
        <td class="text-start">{{ item.awardDescription }}</td>
      </template>
      <template #points="{ item }">
        <td class="text-start">{{ item.points }}</td>
      </template>
      <template #status="{ item }">
        <td class="text-start">{{ formatStatusLabel(item) }}</td>
      </template>
      <template #actions="{ item }">
        <td class="text-end">
          <Actions
            :actions="rowActionsForItem(item)"
            :row-context-label="rowContextLabel(item)"
            @click-action="handleRowAction($event, item)"
          />
        </td>
      </template>
    </DataTable>

    <div
      v-if="state.isLazyLoadingMode && canLoadMoreLazy"
      ref="lazySentinelRef"
      class="behaviour-core-value-lazy-sentinel py-2 text-center text-muted small"
      data-testid="behaviour-positive-behaviours-lazy-sentinel"
      aria-hidden="true"
    >
      {{ $t("behaviour_settings.lazy_scroll_hint") }}
    </div>

    <Pagination
      v-if="
        state.pagination.pages &&
        state.pagination.pages > 1 &&
        !state.isLazyLoadingMode
      "
      :active-page="state.pagination.activePage"
      :pages="state.pagination.pages"
      class="cs-pagination mt-4"
      :dots="false"
      :double-arrows="true"
      size="lg"
      align="center"
      @update:active-page="setActivePage"
    />
  </div>
</template>

<script>
import { formatBehaviourTerminologyTemplate } from "@/configs/behaviourTerminology.js"
import { buildPaginationFromApiResponse } from "@/helpers/behaviourCoreValueApi.js"
import {
  indicatorAriaColorFor,
  indicatorDotClassFor,
  indicatorDotStyleFor
} from "@/helpers/behaviourCoreValueIndicator.js"
import {
  mapPositiveBehaviourSortKey,
  isAutomatedPointsPositiveBehaviourRow
} from "@/helpers/behaviourPositiveBehaviourApi.js"
import { parsePositiveBehaviourApiError } from "@/helpers/behaviourPositiveBehaviourForm.js"
import useAppToast from "@/composables/useAppToast.js"
import behaviourConstants from "@/constants/behaviourManagementConstants.js"
import behaviourCsvUploadConstants from "@/constants/behaviourCsvUploadConstants.js"
import behaviourPositiveBehaviourConstants from "@/constants/behaviourPositiveBehaviourConstants.js"
import { resolveBehaviourCsvUploadModalTitle } from "@/helpers/behaviourCsvUpload.js"
import BehaviourCoreValueLazySelect from "@/v3components/Behaviour/BehaviourCoreValueLazySelect.vue"
import BehaviourCreatePositiveBehaviourForm from "@/v3components/Forms/Behaviour/BehaviourCreatePositiveBehaviourForm.vue"
import BehaviourCsvUploadForm from "@/v3components/Forms/Behaviour/BehaviourCsvUploadForm.vue"
import BehaviourDeletePositiveBehaviourForm from "@/v3components/Forms/Behaviour/BehaviourDeletePositiveBehaviourForm.vue"
import ActionPanel from "@/v3components/shared/DataTable/ActionPanel.vue"
import Actions from "@/v3components/shared/DataTable/Actions.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import DataTable from "@/v3components/shared/DataTable/DataTable.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import Pagination from "@/v3components/shared/DataTable/Pagination.vue"
import PerPage from "@/v3components/shared/DataTable/Perpage.vue"
import {
  computed,
  inject,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch
} from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

const SEARCH_DEBOUNCE_MS = 320

export default {
  name: "BehaviourSettingsPositiveBehavioursTab",
  components: {
    ActionPanel,
    Actions,
    BaseButton,
    BehaviourCoreValueLazySelect,
    CustomSelect,
    DataTable,
    InputField,
    Loader,
    Pagination,
    PerPage
  },
  setup() {
    const { t } = useI18n()
    const store = useStore()
    const modal = inject("modal")
    const toast = useAppToast()
    const lazySentinelRef = ref(null)
    const liveAnnouncement = ref("")
    const statusUpdateId = ref(null)
    let searchDebounceId = null
    let lazyObserver = null

    const showPositiveBehaviourActionError = (error, fallbackMessage) => {
      const parsed = parsePositiveBehaviourApiError(error)
      toast.error(parsed.message || fallbackMessage)
    }

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const noItemsView = computed(() => {
      const bt = behaviourTerminology.value
      return {
        noResults: bt.noPositiveBehavioursRows,
        noItems: bt.noPositiveBehavioursRows
      }
    })

    const statusFilterOptions = computed(() => [
      { label: t("behaviour_settings.status_all"), value: "all" },
      {
        label: behaviourPositiveBehaviourConstants.STATUS.ENABLED,
        value: "active"
      },
      {
        label: behaviourPositiveBehaviourConstants.STATUS.DISABLED,
        value: "inactive"
      }
    ])

    const formatStatusLabel = (item) => {
      if (item?.status === "active") {
        return behaviourPositiveBehaviourConstants.STATUS.ENABLED
      }
      if (item?.status === "inactive") {
        return behaviourPositiveBehaviourConstants.STATUS.DISABLED
      }
      return ""
    }

    const coreValueFilterPrependItems = computed(() => [
      { label: t("behaviour_settings.status_all"), value: "all" }
    ])

    const state = reactive({
      isLoading: true,
      searchInput: "",
      statusFilterValue: "all",
      coreValueFilterValue: "all",
      coreValueFilterSelectedOption: null,
      /** Active table column key used for server-side sort (or null). */
      sortColumn: null,
      /** Server-side sort order: `asc`/`desc` or null. */
      sortOrder: null,
      isLazyAppendInFlight: false,
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        from: 0,
        to: 0,
        per_page: { label: "25", value: 25 }
      },
      isLazyLoadingMode: false,
      lazyLoadedCount: 0,
      tableItems: []
    })

    /** Sorter value bound to the DataTable; mirrors the active sort state. */
    const sorterValue = computed(() => ({
      column: state.sortColumn || "",
      asc: state.sortOrder !== "desc"
    }))

    const rowActionsForItem = (item) => {
      const isAutomatedRule = isAutomatedPointsPositiveBehaviourRow(
        item,
        behaviourTerminology.value
      )
      const actions = []

      if (!isAutomatedRule) {
        actions.push(
          {
            label: t("behaviour_settings.action_edit"),
            icon: "ri-pencil-line",
            action: "edit"
          },
          {
            label: t("behaviour_settings.action_delete"),
            icon: "ri-delete-bin-line",
            class: "important",
            action: "delete"
          }
        )
      }

      if (item?.status === "active") {
        actions.push({
          label: t("behaviour_settings.action_disable"),
          icon: "ri-forbid-line",
          action: "disable"
        })
      } else if (item?.status === "inactive") {
        actions.push({
          label: t("behaviour_settings.action_enable"),
          icon: "ri-checkbox-circle-line",
          action: "enable"
        })
      }
      return actions
    }

    const rowContextLabel = (item) => {
      const bt = store.getters["behaviourSettings/behaviourTerminology"]
      const name = item?.name
      if (name) return String(name)
      if (item?.id != null) {
        return `${bt.positiveBehaviours || "Positive behaviour"} ${item.id}`
      }
      return bt.positiveBehaviours || "Positive behaviour"
    }

    const tableFields = computed(() => {
      const bt = behaviourTerminology.value
      return [
        {
          key: "name",
          label: bt.positiveBehaviours,
          sorter: true,
          filter: false
        },
        {
          key: "coreValueName",
          label: bt.coreValue,
          sorter: true,
          filter: false
        },
        {
          key: "awardDescription",
          label: bt.colAwardDescription,
          sorter: true,
          filter: false
        },
        {
          key: "points",
          label: bt.colPoints,
          sorter: true,
          filter: false
        },
        {
          key: "status",
          label: t("behaviour_settings.col_status"),
          sorter: true,
          filter: false
        },
        {
          key: "actions",
          label: t("behaviour_settings.col_actions"),
          sorter: false,
          filter: false,
          _style: "width: 96px;"
        }
      ]
    })

    const resolveCoreValueFilterId = () => {
      const cv = state.coreValueFilterValue
      if (cv == null || cv === "" || String(cv) === "all") {
        return null
      }
      return cv
    }

    const announce = (message) => {
      liveAnnouncement.value = ""
      if (!message) return
      requestAnimationFrame(() => {
        liveAnnouncement.value = message
      })
    }

    const fetchPositiveBehavioursPage = async ({
      page,
      append = false
    } = {}) => {
      const perPageVal = Number(state.pagination.per_page?.value) || 25
      const perPageLabel =
        state.pagination.per_page?.label || String(perPageVal)
      const isLazyMode =
        perPageVal === behaviourConstants.CORE_VALUE_TABLE_ALL_ENTRIES_PER_PAGE
      const lazyChunkSize =
        behaviourConstants.CORE_VALUE_TABLE_ALL_ENTRIES_PER_PAGE
      const requestPerPage = isLazyMode ? lazyChunkSize : perPageVal
      const requestPage = page ?? state.pagination.activePage ?? 1

      const { rows, pagination } = await store.dispatch(
        "behaviourSettings/fetchPositiveBehavioursList",
        {
          page: requestPage,
          perPage: requestPerPage,
          search: state.searchInput,
          statusFilter: state.statusFilterValue || "all",
          coreValueId: resolveCoreValueFilterId(),
          sortBy: mapPositiveBehaviourSortKey(state.sortColumn),
          sortOrder: state.sortOrder
        }
      )

      state.isLazyLoadingMode = isLazyMode
      state.pagination = buildPaginationFromApiResponse(pagination, {
        perPageLabel,
        perPageValue: isLazyMode ? lazyChunkSize : perPageVal
      })
      state.pagination.per_page = { label: perPageLabel, value: perPageVal }

      if (isLazyMode && append) {
        state.tableItems = [...state.tableItems, ...rows]
        state.lazyLoadedCount = state.tableItems.length
      } else {
        state.tableItems = rows
        state.lazyLoadedCount = rows.length
      }

      return { rows, isLazyMode }
    }

    const refreshTable = async ({ append = false } = {}) => {
      try {
        if (append) {
          state.isLazyAppendInFlight = true
        } else {
          state.isLoading = true
        }

        const requestPage = append
          ? (Number(state.pagination.activePage) || 1) + 1
          : Number(state.pagination.activePage) || 1

        const { isLazyMode } = await fetchPositiveBehavioursPage({
          page: requestPage,
          append
        })

        if (append && isLazyMode) {
          state.pagination.activePage = requestPage
        }

        const bt = behaviourTerminology.value
        const total = state.pagination.total || 0
        const count = state.tableItems.length

        if (isLazyMode) {
          announce(
            formatBehaviourTerminologyTemplate(
              bt.positiveBehavioursAnnounceLoadedLazy,
              { count, total }
            )
          )
        } else {
          announce(
            formatBehaviourTerminologyTemplate(
              bt.positiveBehavioursAnnounceLoadedPaginated,
              { count, total }
            )
          )
        }
      } catch {
        if (!append) {
          state.tableItems = []
          state.pagination = {
            ...state.pagination,
            total: 0,
            pages: 0,
            from: 0,
            to: 0
          }
        }
        announce(
          t("behaviour_settings.positive_behaviour_list_load_error", {
            positiveBehaviours: behaviourTerminology.value.positiveBehaviours
          })
        )
      } finally {
        state.isLoading = false
        state.isLazyAppendInFlight = false
        await nextTick()
        setupLazyObserver()
      }
    }

    const canLoadMoreLazy = computed(() => {
      if (!state.isLazyLoadingMode) return false
      const currentPage = Number(state.pagination.activePage) || 1
      const totalPages = Number(state.pagination.pages) || 1
      return currentPage < totalPages
    })

    const appendLazyChunk = async () => {
      if (
        !state.isLazyLoadingMode ||
        state.isLoading ||
        state.isLazyAppendInFlight ||
        !canLoadMoreLazy.value
      ) {
        return
      }

      const previousVisible = state.tableItems.length
      await refreshTable({ append: true })

      const bt = behaviourTerminology.value
      announce(
        formatBehaviourTerminologyTemplate(
          bt.positiveBehavioursAnnounceLazyMore,
          {
            visible: state.tableItems.length,
            total: state.pagination.total || 0
          }
        )
      )

      if (state.tableItems.length === previousVisible) {
        teardownLazyObserver()
      }
    }

    const teardownLazyObserver = () => {
      if (lazyObserver) {
        lazyObserver.disconnect()
        lazyObserver = null
      }
    }

    const setupLazyObserver = () => {
      teardownLazyObserver()
      if (!state.isLazyLoadingMode || !canLoadMoreLazy.value) return
      const el = lazySentinelRef.value
      if (!el || !("IntersectionObserver" in window)) return
      lazyObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries
          if (entry?.isIntersecting) {
            appendLazyChunk()
          }
        },
        { root: null, rootMargin: "80px", threshold: 0.15 }
      )
      lazyObserver.observe(el)
    }

    watch(
      () => [state.isLazyLoadingMode, canLoadMoreLazy.value],
      () => {
        nextTick(() => setupLazyObserver())
      }
    )

    const onSearchFieldInput = (value) => {
      state.searchInput = value || ""
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
      }
      searchDebounceId = setTimeout(() => {
        state.pagination.activePage = 1
        refreshTable()
      }, SEARCH_DEBOUNCE_MS)
    }

    const resetFilters = () => {
      if (state.isLoading) return
      state.searchInput = ""
      state.statusFilterValue = "all"
      state.coreValueFilterValue = "all"
      state.coreValueFilterSelectedOption = null
      state.sortColumn = null
      state.sortOrder = null
      state.pagination.activePage = 1
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
        searchDebounceId = null
      }
      refreshTable()
    }

    const onStatusFilterChange = () => {
      state.pagination.activePage = 1
      refreshTable()
    }

    /**
     * Called by `<DataTable>` when the user clicks a sortable column header.
     * With `resetable: false`, the same column toggles asc → desc → asc.
     * Columns whose key has no API sort mapping are ignored.
     */
    const onSortColumnChange = (sorterState) => {
      const column = String(sorterState?.column ?? "").trim()
      if (!column) {
        state.sortColumn = null
        state.sortOrder = null
      } else if (mapPositiveBehaviourSortKey(column)) {
        state.sortColumn = column
        state.sortOrder = sorterState?.asc ? "asc" : "desc"
      } else {
        return
      }
      state.pagination.activePage = 1
      refreshTable()
    }

    const onCoreValueFilterChange = (value) => {
      state.coreValueFilterValue = value
      if (value == null || value === "" || String(value) === "all") {
        state.coreValueFilterSelectedOption = null
      }
      state.pagination.activePage = 1
      refreshTable()
    }

    const onCoreValueFilterOptionSelected = (option) => {
      if (!option) return
      state.coreValueFilterSelectedOption =
        option.value === "all" ? null : { ...option }
    }

    const setPerPage = (option) => {
      if (!option) return
      state.pagination.per_page = option
      state.pagination.activePage = 1
      refreshTable()
    }

    const setActivePage = (page) => {
      state.pagination.activePage = page
      refreshTable()
    }

    const getExistingNamesExcluding = (excludeId) =>
      state.tableItems
        .filter((row) => row?.id !== excludeId)
        .map((row) => row?.name)
        .filter(Boolean)

    const openEditPositiveBehaviour = (item) => {
      if (!item?.id) return
      modal?.open?.(BehaviourCreatePositiveBehaviourForm, {
        size: "lg",
        title: t("behaviour_settings.edit_positive_behaviour_title", {
          positiveBehaviours: behaviourTerminology.value.positiveBehaviours
        }),
        props: {
          editablePositiveBehaviour: { ...item },
          existingNames: getExistingNamesExcluding(item.id)
        }
      })
    }

    const openDeletePositiveBehaviour = (item) => {
      if (!item?.id) return
      modal?.open?.(BehaviourDeletePositiveBehaviourForm, {
        size: "lg",
        title: t("behaviour_settings.delete_positive_behaviour_title", {
          positiveBehaviours: behaviourTerminology.value.positiveBehaviours
        }),
        props: {
          positiveBehaviour: { ...item }
        }
      })
    }

    const setPositiveBehaviourStatus = async (item, nextStatus) => {
      if (!item?.id || !nextStatus || statusUpdateId.value != null) {
        return false
      }
      if (item.status === nextStatus) {
        return false
      }

      try {
        statusUpdateId.value = item.id
        await store.dispatch(
          "behaviourSettings/updatePositiveBehaviourStatus",
          {
            id: item.id,
            existingRow: item,
            status: nextStatus
          }
        )
        const index = state.tableItems.findIndex((row) => row?.id === item.id)
        if (index >= 0) {
          state.tableItems[index] = {
            ...state.tableItems[index],
            status: nextStatus
          }
        }
        return true
      } catch (error) {
        showPositiveBehaviourActionError(
          error,
          t("behaviour_settings.positive_behaviour_updated_error", {
            positiveBehaviours: behaviourTerminology.value.positiveBehaviours
          })
        )
        return false
      } finally {
        statusUpdateId.value = null
      }
    }

    const handleRowAction = async (action, item) => {
      const key = action?.action
      if (!key || !item) return
      switch (key) {
        case "disable":
          if (item?.status === "active") {
            const disabled = await setPositiveBehaviourStatus(item, "inactive")
            if (disabled) {
              announce(
                t("behaviour_settings.positive_behaviour_disabled_success", {
                  positiveBehaviours:
                    behaviourTerminology.value.positiveBehaviours
                })
              )
            }
          }
          break
        case "enable":
          if (item?.status === "inactive") {
            const enabled = await setPositiveBehaviourStatus(item, "active")
            if (enabled) {
              announce(
                t("behaviour_settings.positive_behaviour_enabled_success", {
                  positiveBehaviours:
                    behaviourTerminology.value.positiveBehaviours
                })
              )
            }
          }
          break
        case "edit":
          openEditPositiveBehaviour(item)
          break
        case "delete":
          openDeletePositiveBehaviour(item)
          break
        default:
          break
      }
    }

    const onAddPositiveBehaviour = () => {
      const bt = behaviourTerminology.value
      modal?.open?.(BehaviourCreatePositiveBehaviourForm, {
        size: "lg",
        title: bt.addPositiveBehaviour,
        props: {
          existingNames: getExistingNamesExcluding(null)
        }
      })
    }

    const clearModalAction = () => {
      if (!modal?.options) return
      modal.options.action = ""
      modal.options.actionData = null
    }

    watch(
      () => modal?.options?.action,
      (action) => {
        const actionData = modal?.options?.actionData
        if (!action || !actionData) return

        const positiveBehaviourTerm =
          behaviourTerminology.value.positiveBehaviours

        if (action === "positiveBehaviourCreated") {
          state.pagination.activePage = 1
          clearModalAction()
          refreshTable()
          announce(
            t("behaviour_settings.positive_behaviour_created_success", {
              positiveBehaviours: positiveBehaviourTerm
            })
          )
          return
        }

        if (action === "positiveBehaviourUpdated") {
          clearModalAction()
          refreshTable()
          announce(
            t("behaviour_settings.positive_behaviour_updated_success", {
              positiveBehaviours: positiveBehaviourTerm
            })
          )
          return
        }

        if (action === "positiveBehaviourDeleted") {
          clearModalAction()
          refreshTable()
          announce(
            t("behaviour_settings.positive_behaviour_deleted_success", {
              positiveBehaviours: positiveBehaviourTerm
            })
          )
          return
        }

        if (action === "positiveBehavioursCsvUploaded") {
          state.pagination.activePage = 1
          clearModalAction()
          refreshTable()
          const inserted = Number(actionData?.inserted) || 0
          const failed = Number(actionData?.failed) || 0
          if (failed > 0) {
            announce(
              t("behaviour_settings.upload_csv_partial_success", {
                inserted,
                failed,
                total: Number(actionData?.totalRows) || inserted + failed
              })
            )
          } else {
            announce(
              t("behaviour_settings.upload_csv_success", {
                inserted,
                total: Number(actionData?.totalRows) || inserted
              })
            )
          }
        }
      }
    )

    const onUploadCsv = () => {
      const bt = behaviourTerminology.value
      modal?.open?.(BehaviourCsvUploadForm, {
        size: "sm",
        title: resolveBehaviourCsvUploadModalTitle(
          behaviourCsvUploadConstants.UPLOAD_KIND.POSITIVE_BEHAVIOUR,
          bt,
          t
        ),
        props: {
          uploadKind: behaviourCsvUploadConstants.UPLOAD_KIND.POSITIVE_BEHAVIOUR
        }
      })
    }

    onMounted(() => {
      store.dispatch("behaviourSettings/fetchBehaviourTerminology")
      refreshTable()
    })

    onUnmounted(() => {
      teardownLazyObserver()
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
      }
    })

    return {
      state,
      behaviourTerminology,
      noItemsView,
      tableFields,
      rowActionsForItem,
      rowContextLabel,
      formatStatusLabel,
      statusFilterOptions,
      coreValueFilterPrependItems,
      liveAnnouncement,
      lazySentinelRef,
      canLoadMoreLazy,
      onSearchFieldInput,
      resetFilters,
      onStatusFilterChange,
      onSortColumnChange,
      sorterValue,
      onCoreValueFilterChange,
      onCoreValueFilterOptionSelected,
      setPerPage,
      setActivePage,
      handleRowAction,
      onAddPositiveBehaviour,
      onUploadCsv,
      indicatorDotClass: indicatorDotClassFor,
      indicatorDotStyle: indicatorDotStyleFor,
      indicatorAriaLabel: (item) =>
        t("behaviour_settings.indicator_a11y", {
          color: indicatorAriaColorFor(item)
        })
    }
  }
}
</script>
